import React     from 'react';
import View       from '@widesk-ui/components/View';
import Alert      from '@widesk-ui/components/Alert';
import Button     from '@widesk-ui/components/Button';
import Input      from '@widesk-ui/components/form/Input';
import useTheme   from '@widesk-ui/hooks/useTheme';
import { LeftOutlined }        from '@ant-design/icons';
import { RightOutlined }       from '@ant-design/icons';
import { arbitrationOf }    from '@/TokamapEditor/helpers/planDoubts';
import { defaultArbitration } from '@/TokamapEditor/helpers/planDoubts';
import { doubtKey }         from '@/TokamapEditor/helpers/planDoubts';
import { isSettled }        from '@/TokamapEditor/helpers/planDoubts';
import { remainingCount }   from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitration }  from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitrations } from '@/TokamapEditor/helpers/planDoubts';
import type { PlanCorrections }  from '@/TokamapEditor/helpers/planDoubts';
import type { PlanDoubt }        from '@models/map/dxfTypes';

/**
 * **La file des doutes** — une carte à la fois (`docs/PLAN_PARCOURS_IMPORT.md` §4).
 *
 * ## Pourquoi une carte et pas une liste
 *
 * Une liste de 45 anomalies se lit comme un rapport : on la parcourt des yeux et on ne corrige
 * rien. Une carte pose UNE question, montre la forme sur la carte, et attend deux touches. La
 * différence n'est pas cosmétique : c'est ce qui fait passer six doutes en une minute plutôt
 * qu'en cinq.
 *
 * ## Un doute tranché ne quitte pas la file
 *
 * Il change de couleur. Sans quoi l'opérateur perd le compte de ce qu'il a vu, et ne peut plus
 * revenir sur un arbitrage — or il le voudra, c'est le premier réflexe quand on découvre la
 * suivante. Rien n'est écrit avant l'étape 3 de toute façon.
 *
 * ## Les raccourcis
 *
 * `G` garde, `E` écarte, les flèches naviguent. Ils ne sont actifs que lorsque le focus n'est
 * pas dans un champ de saisie — sans quoi corriger un numéro en `E`3 déclencherait « écarter »
 * à la première frappe.
 */
type Props = {
	doubts: PlanDoubt[];
	arbitrations: PlanArbitrations;
	corrections: PlanCorrections;
	/** Index de la carte affichée, piloté par le parent (il pilote aussi le zoom carte). */
	current: number;
	disabled?: boolean;
	onGo: (index: number) => void;
	onArbitrate: (doubt: PlanDoubt, decision: PlanArbitration) => void;
	onCorrect: (doubt: PlanDoubt, value: string) => void;
};

/** Ce que l'écran promet pour chaque décision, dans les mots de l'opérateur. */
const OUTCOMES: Record<PlanArbitration, string> = {
	kept: 'sera importée',
	discarded: 'ne sera pas importée',
};

const PlanDoubtQueue = ({ doubts, arbitrations, corrections, current, disabled, onGo, onArbitrate, onCorrect }: Props) => {
	const doubt = doubts[current];
	const remaining = remainingCount(doubts, arbitrations);
	// ⚠️ **Aucune couleur en dur ici.** L'application se porte aussi en thème SOMBRE : un fond
	// jaune pâle et un texte laissé à l'héritage y donnaient une carte illisible — titre et
	// motif en sombre sur sombre. Les jetons d'antd suivent le thème, une valeur hexadécimale
	// non. La teinte d'alerte est donc DÉRIVÉE de `colorWarning` par transparence (`#rrggbbaa`),
	// ce qui la pose correctement sur un fond clair comme sur un fond sombre.
	const theme = useTheme();

	// Les raccourcis vivent sur la fenêtre, pas sur la carte : l'opérateur enchaîne au clavier
	// sans jamais avoir à cliquer dans le panneau pour lui rendre le focus.
	React.useEffect(() => {
		if (!doubt || disabled) return;

		const onKey = (event: KeyboardEvent) => {
			// Une frappe dans un champ de saisie est une frappe, pas un raccourci : corriger
			// un numéro « E3 » déclencherait sinon « écarter » à la première lettre.
			const target = event.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
			if (event.metaKey || event.ctrlKey || event.altKey) return;

			const key = event.key.toLowerCase();
			if ('g' === key && doubt.keepable) onArbitrate(doubt, 'kept');
			else if ('e' === key) onArbitrate(doubt, 'discarded');
			else if ('arrowright' === event.key) onGo((current + 1) % doubts.length);
			else if ('arrowleft' === event.key) onGo((current - 1 + doubts.length) % doubts.length);
			else return;

			event.preventDefault();
		};

		window.addEventListener('keydown', onKey);

		return () => window.removeEventListener('keydown', onKey);
	}, [current, doubt, doubts.length, disabled, onArbitrate, onGo]);

	if (!doubts.length) {
		return (
			<Alert
				type="success"
				showIcon
				message="Rien à vérifier"
				description="Aucune forme de ce plan ne demande d’arbitrage : le mapping peut être validé tel quel."
			/>
		);
	}

	if (!doubt) return null;

	const key = doubtKey(doubt);
	const decision = arbitrationOf(doubt, arbitrations);
	const settled = isSettled(doubt, arbitrations);
	// Le motif porte une suggestion quand le serveur sait quoi proposer — le numéro qui manque
	// entre deux voisins, ou celui qui a été retenu quand il y en avait deux.
	const correctable = null !== doubt.suggestion || 'glyph_unreadable' === doubt.kind || 'no_number' === doubt.kind;

	return (
		<View
			gap={8}
			style={{
				border: `1px solid ${theme.colorWarning}66`,
				borderRadius: theme.borderRadius,
				padding: 12,
				background: `${theme.colorWarning}1a`,
				color: theme.colorText,
			}}
		>
			<View row gap={8} center>
				{/* Couleur EXPLICITE, pas héritée : `View` pose la sienne, et le `color` du
				    conteneur ne descend pas jusqu'ici. C'est ce titre qui était illisible en
				    thème sombre. */}
				<View flex size={13} bold color={theme.colorText}>
					À vérifier — {current + 1} / {doubts.length}
				</View>
				<View size={11} color={theme.colorTextDescription}>
					{remaining ? `${remaining} non regardée(s)` : 'toutes regardées'}
				</View>
				<Button
					type="text"
					size="small"
					icon={<LeftOutlined />}
					title="Précédente (←)"
					onClick={() => onGo((current - 1 + doubts.length) % doubts.length)}
				/>
				<Button
					type="text"
					size="small"
					icon={<RightOutlined />}
					title="Suivante (→)"
					onClick={() => onGo((current + 1) % doubts.length)}
				/>
			</View>

			<View gap={2}>
				<View size={13} bold color={theme.colorText}>{doubt.label}</View>
				<View size={12} color={theme.colorText}>{doubt.reason}</View>
				<View size={11} color={theme.colorTextDescription}>
					Calque « {doubt.layer} », forme n° {doubt.index}
					{/* La forme est surlignée et cadrée sur la carte par le parent : le dire
					    évite que l'opérateur cherche où regarder. */}
					{' '}— surlignée sur la carte.
				</View>
			</View>

			{correctable && (
				<View gap={4}>
					<Input
						placeholder={doubt.suggestion ? `Numéro proposé : ${doubt.suggestion}` : 'Numéro de ce stand'}
						value={corrections[key] ?? doubt.suggestion ?? ''}
						disabled={disabled}
						onChange={(value: unknown) => onCorrect(doubt, String(value ?? ''))}
					/>
					<View size={11} color={theme.colorTextDescription}>
						{/* Le numéro saisi part réellement au serveur, dans `layerMapping[].numbers`,
						    et ÉCRASE ce que l'appariement avait trouvé. Le dire évite que
						    l'opérateur double la saisie dans l'éditeur après coup. */}
						Ce numéro sera posé sur le POI, à la place de celui que le plan portait.
					</View>
				</View>
			)}

			<View row gap={8}>
				{doubt.keepable && (
					<Button
						size="small"
						type={'kept' === decision ? 'primary' : 'default'}
						disabled={disabled}
						onClick={() => onArbitrate(doubt, 'kept')}
					>
						Garder (G)
					</Button>
				)}
				<Button
					size="small"
					danger={'discarded' === decision}
					type={'discarded' === decision ? 'primary' : 'default'}
					disabled={disabled}
					onClick={() => onArbitrate(doubt, 'discarded')}
				>
					Écarter (E)
				</Button>
				<View flex />
				<View size={11} color={settled ? theme.colorSuccess : theme.colorTextDescription} style={{ alignSelf: 'center' }}>
					{settled
						? `Décidé : ${OUTCOMES[decision]}`
						: `Par défaut : ${OUTCOMES[defaultArbitration(doubt)]}`}
				</View>
			</View>
		</View>
	);
};

export default PlanDoubtQueue;
